// Module: submitErrorText
// Role: Map a checkpoint-submit / apiFetch error to a friendly, user-facing
//       Russian message. Single source of truth for the flow error surface.
// Used by: FlowScreen SubmitErrorBanner, MultiImageUploadBrick, UrlInputBrick,
//          UgcTextScreen catch blocks.
//
// Why this exists: apiFetch (api/client.ts) attaches structured fields
// (status, code, retryAfter) AND a back-compat `err.message` of the form
// `API <status>: <raw body>`. The photo brick used to surface that raw body
// verbatim ("Ошибка отправки: API 429: {…json…}") — an ugly, leaky error.
// NEVER show the raw body / machine code to the user.

interface SubmitErrorShape {
  status?: number;
  code?: string;
  retryAfter?: number | null;
  message?: string;
}

/** Human-format a wait interval in seconds → "45 сек" / "3 мин". */
function formatWait(sec: number): string {
  if (sec < 60) return `${sec} сек`;
  return `${Math.ceil(sec / 60)} мин`;
}

/**
 * Friendly message for a submit error. Recognises rate-limit (429 /
 * FLOW_RATE_LIMITED) and max-attempts (409) via structured fields with a
 * fallback to the legacy `API <status>:` message prefix. Wait time is shown
 * only when the backend supplied a positive Retry-After.
 */
export function submitErrorText(error: unknown): string {
  const e = (error ?? null) as SubmitErrorShape | null;
  const msg = e?.message ?? '';

  const is409 = e?.status === 409 || msg.startsWith('API 409');
  if (is409) return 'Достигнут лимит попыток. Обратитесь в поддержку.';

  const is429 =
    e?.status === 429 || e?.code === 'FLOW_RATE_LIMITED' || msg.startsWith('API 429');
  if (is429) {
    const sec = typeof e?.retryAfter === 'number' && e.retryAfter > 0 ? e.retryAfter : null;
    return sec === null
      ? 'Слишком много запросов. Подождите немного и попробуйте снова.'
      : `Слишком много запросов. Подождите ${formatWait(sec)} и попробуйте снова.`;
  }

  return 'Произошла ошибка. Попробуйте ещё раз.';
}
